import React, { useState } from 'react';
import { useActivityList } from './hooks/useActivityList';
import { cn } from '@/utils/cn';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { TopbarSearch } from '@/components/ui/TopbarSearch';
import { Card, Badge, Button } from '@/components/ui';
import { 
  Filter, 
  History, 
  User as UserIcon, 
  Layers, 
  Calendar,
  Download,
  Info,
  ChevronRight,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Database
} from 'lucide-react';
import { ActivityDetailDrawer } from './components/ActivityDetailDrawer';

export function ActivityListPage() {
  const { logs, totalCount, users, filters, isLoading, setFilters, refresh } = useActivityList();
  const [selectedLog, setSelectedLog] = useState<any | null>(null);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE': return <i className="ri-add-circle-line" />;
      case 'UPDATE': return <i className="ri-edit-box-line" />;
      case 'DELETE': return <i className="ri-delete-bin-line" />;
      case 'LOGIN': return <i className="ri-login-circle-line" />;
      case 'LOGOUT': return <i className="ri-logout-circle-line" />;
      default: return <i className="ri-history-line" />;
    }
  };

  const getActionVariant = (action: string): any => {
    switch (action) {
      case 'CREATE': return 'glow-success';
      case 'UPDATE': return 'glow-warning';
      case 'DELETE': return 'glow-danger';
      case 'LOGIN': return 'glow-info';
      case 'LOGOUT': return 'glow-purple';
      default: return 'default';
    }
  };

  const getDeviceIcon = (device?: string) => {
    if (device === 'mobile') return <Smartphone className="w-3 h-3" />;
    if (device === 'tablet') return <Tablet className="w-3 h-3" />;
    return <Monitor className="w-3 h-3" />;
  };

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-500">
      {/* Header chuẩn Landscape */}
      <PageHeaderNew 
        title="Nhật ký hoạt động"
        subtitle="Theo dõi và truy vết mọi thay đổi trong hệ thống của bạn"
        backUrl="/settings"
      />

      {/* Toolbar & Filters */}
      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-row items-center gap-2 flex-1">
          <div className="flex-1 min-w-0">
            <TopbarSearch
              placeholder="Tìm theo nội dung..."
              value={filters.query}
              onChange={(query) => setFilters({ query })}
            />
          </div>
        </div>
        
        <div className="flex flex-row items-center gap-2 overflow-x-auto">
          <select 
            className="h-10 shrink-0 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none w-[110px] sm:w-[130px]"
            value={filters.module}
            onChange={(e) => setFilters({ module: e.target.value })}
          >
            <option value="all">Tất cả phân hệ</option>
            <option value="auth">Xác thực</option>
            <option value="projects">Dự án</option>
            <option value="inventory">Vật tư</option>
            <option value="settings">Cài đặt</option>
          </select>

          <select 
            className="h-10 shrink-0 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none w-[130px] sm:w-[150px]"
            value={filters.userId}
            onChange={(e) => setFilters({ userId: e.target.value })}
          >
            <option value="all">Tất cả người dùng</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>

          <div className="h-10 flex shrink-0 items-center px-2 rounded-md border border-slate-200 bg-white gap-2">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <input 
              type="date" 
              className="bg-transparent text-xs text-slate-700 outline-none w-[100px]"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ dateFrom: e.target.value })}
            />
          </div>

          <Button variant="outline" className="h-10 shrink-0 px-3 text-xs text-slate-600 border-slate-200 bg-white">
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Xuất Excel
          </Button>

          <Button 
            variant="ghost" 
            className="h-10 shrink-0 px-3 text-xs text-slate-400"
            onClick={() => setFilters({ 
              query: '', 
              module: 'all', 
              action: 'all', 
              userId: 'all', 
              dateFrom: '', 
              dateTo: '' 
            })}
          >
            Đặt lại
          </Button>
        </div>
      </div>

      {/* List Content */}
      <div className="rounded-md border border-slate-100 bg-white overflow-hidden shadow-sm flex flex-col h-[calc(100vh-220px)]">
        <div className="divide-y divide-slate-50 flex-1 overflow-y-auto custom-scrollbar">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 rounded-full bg-slate-50" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/4 rounded bg-slate-50" />
                  <div className="h-3 w-1/2 rounded bg-slate-50" />
                </div>
              </div>
            ))
          ) : logs.length > 0 ? (
            logs.map((log) => (
              <div 
                key={log.id} 
                onClick={() => setSelectedLog(log)}
                className="p-4 flex items-start gap-4 hover:bg-slate-50/50 transition-all cursor-pointer group relative"
              >
                {/* Action Icon */}
                <Badge 
                  variant={getActionVariant(log.actionCode)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full p-0 border-0"
                >
                  {getActionIcon(log.actionCode)}
                </Badge>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-xs font-medium text-slate-900 line-clamp-1">{log.summary}</p>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                       <span className="text-[10px] text-slate-400 font-normal">
                        {new Date(log.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                       </span>
                       <ChevronRight className="w-4 h-4 text-slate-300 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>

                  <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1.5">
                    {/* Actor */}
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center">
                        <UserIcon className="w-2.5 h-2.5 text-slate-400" />
                      </div>
                      <span className="text-xs text-slate-600">{log.actorName}</span>
                    </div>

                    {/* Module */}
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center">
                        <Database className="w-2.5 h-2.5 text-slate-400" />
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{log.moduleCode}</span>
                    </div>

                    {/* Device Meta */}
                    {(log.browser || log.os) && (
                      <div className="flex items-center gap-2 text-[10px] text-slate-400 border-l border-slate-100 pl-4">
                        <div className="flex items-center gap-1">
                          {getDeviceIcon(log.deviceType)}
                          <span>{log.browser}</span>
                        </div>
                        <span>•</span>
                        <span>{log.os}</span>
                        {log.ipAddress && (
                          <>
                            <span>•</span>
                            <div className="flex items-center gap-1">
                              <Globe className="w-2.5 h-2.5" />
                              <span>{log.ipAddress}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="py-24 text-center">
              <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                <History className="w-8 h-8" />
              </div>
              <p className="text-xs text-slate-900 font-medium">Không tìm thấy hoạt động nào</p>
              <p className="text-xs text-slate-400 mt-1">Thử điều chỉnh bộ lọc hoặc từ khóa tìm kiếm.</p>
            </div>
          )}
        </div>

        {/* Pagination placeholder */}
        {totalCount > filters.pageSize && (
          <div className="p-4 border-t border-slate-50 flex items-center justify-between bg-white shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
            <p className="text-xs text-slate-500">
              Hiển thị <span className="font-medium text-slate-900">{logs.length}</span> trên <span className="font-medium text-slate-900">{totalCount}</span> hoạt động
            </p>
            <div className="flex gap-1">
              <Button 
                variant="outline" 
                className="h-8 w-8 p-0 border-slate-200 hover:bg-slate-50 text-slate-600" 
                disabled={filters.page === 1}
                onClick={() => setFilters({ page: filters.page - 1 })}
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </Button>
              <Button 
                variant="outline" 
                className="h-8 w-8 p-0 border-slate-200 hover:bg-slate-50 text-slate-600"
                disabled={filters.page * filters.pageSize >= totalCount}
                onClick={() => setFilters({ page: filters.page + 1 })}
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ActivityDetailDrawer 
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        log={selectedLog}
      />
    </div>
  );
}
